import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../api';
import './DashboardPages.css';

const VEHICLE_KEYS = ['cargo_motorbike', 'pickup', 'light_truck', 'truck'];

function SettingsPage() {
  const { admin } = useAuth();
  const [activeSection, setActiveSection] = useState('account');
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordError, setPasswordError] = useState(null);
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [saving, setSaving] = useState(false);

  const [pricingConfig, setPricingConfig] = useState(null);
  const [pricingError, setPricingError] = useState(null);
  const [pricingSuccess, setPricingSuccess] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);

  const [serviceAreas, setServiceAreas] = useState([]);
  const [areasError, setAreasError] = useState(null);
  const [areasFilter, setAreasFilter] = useState('');

  useEffect(() => {
    if (activeSection === 'pricing') loadPricing();
    if (activeSection === 'areas') loadAreas();
  }, [activeSection]);

  const loadPricing = async () => {
    try {
      setPricingError(null);
      const { config } = await api.getPricingConfig();
      setPricingConfig(config);
      const verify = await api.verifyPricing();
      setVerifyResult(verify);
    } catch (err) {
      setPricingError(err.message);
    }
  };

  const loadAreas = async () => {
    try {
      setAreasError(null);
      const { areas } = await api.getServiceAreas();
      setServiceAreas(areas);
    } catch (err) {
      setAreasError(err.message);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordError(null);
    setPasswordSuccess(false);

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }
    if (passwordForm.newPassword.length < 6) {
      setPasswordError('Password must be at least 6 characters');
      return;
    }

    try {
      setSaving(true);
      await api.changePassword(admin.username, passwordForm.currentPassword, passwordForm.newPassword);
      setPasswordSuccess(true);
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      setPasswordError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const updateVehicle = (key, field, value) => {
    setPricingConfig((prev) => ({
      ...prev,
      modeA: {
        ...prev.modeA,
        vehicles: {
          ...prev.modeA.vehicles,
          [key]: { ...prev.modeA.vehicles[key], [field]: Number(value) || 0 },
        },
      },
    }));
  };

  const updateCoeff = (field, value) => {
    setPricingConfig((prev) => ({
      ...prev,
      modeA: {
        ...prev.modeA,
        coefficients: { ...prev.modeA.coefficients, [field]: parseFloat(value) || 0 },
      },
    }));
  };

  const updateModeBPrice = (band, unit, value) => {
    setPricingConfig((prev) => ({
      ...prev,
      modeB: {
        ...prev.modeB,
        bands: {
          ...prev.modeB.bands,
          [band]: {
            ...prev.modeB.bands[band],
            prices: { ...prev.modeB.bands[band].prices, [unit]: Number(value) || 0 },
          },
        },
      },
    }));
  };

  const savePricing = async () => {
    try {
      setSaving(true);
      setPricingError(null);
      setPricingSuccess(false);
      await api.savePricingConfig(pricingConfig);
      const verify = await api.verifyPricing();
      setVerifyResult(verify);
      setPricingSuccess(true);
    } catch (err) {
      setPricingError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleArea = async (area) => {
    try {
      setAreasError(null);
      await api.updateServiceArea(area.area_id, { active: !area.active });
      loadAreas();
    } catch (err) {
      setAreasError(err.message);
    }
  };

  const saveAreaCoords = async (area) => {
    try {
      setAreasError(null);
      setSaving(true);
      const latRaw = area._editLat != null ? area._editLat : (area.lat != null ? String(area.lat) : '');
      const lngRaw = area._editLng != null ? area._editLng : (area.lng != null ? String(area.lng) : '');
      await api.updateServiceArea(area.area_id, {
        lat: latRaw === '' ? null : Number(latRaw),
        lng: lngRaw === '' ? null : Number(lngRaw),
        road_access: area._editAccess || area.road_access || 'all',
      });
      await loadAreas();
    } catch (err) {
      setAreasError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const patchAreaLocal = (areaId, patch) => {
    setServiceAreas((prev) =>
      prev.map((a) => (a.area_id === areaId ? { ...a, ...patch } : a))
    );
  };

  const filteredAreas = serviceAreas.filter((a) => {
    const q = areasFilter.trim().toLowerCase();
    if (!q) return true;
    return (
      a.name.toLowerCase().includes(q) ||
      (a.region || '').toLowerCase().includes(q) ||
      (a.zone || '').toLowerCase().includes(q)
    );
  });

  return (
    <div className="dashboard-page">
      <h1>Settings</h1>
      <p className="dashboard-subtitle">Manage account, pricing grid, and service areas</p>

      <div className="settings-layout">
        <nav className="settings-nav">
          {['account', 'pricing', 'areas', 'notifications'].map((key) => (
            <button
              key={key}
              type="button"
              className={`settings-nav-item ${activeSection === key ? 'active' : ''}`}
              onClick={() => setActiveSection(key)}
            >
              {key === 'account' && 'Account'}
              {key === 'pricing' && 'Pricing Grid'}
              {key === 'areas' && 'Service Areas'}
              {key === 'notifications' && 'Notifications'}
            </button>
          ))}
        </nav>

        <div className="settings-content">
          {activeSection === 'account' && (
            <div className="settings-section-card">
              <h2>Account</h2>
              <p className="settings-desc">Update your password. Username: <strong>{admin?.username}</strong></p>
              <form onSubmit={handlePasswordChange} className="settings-form">
                <div className="form-group">
                  <label htmlFor="current-password">Current password</label>
                  <input id="current-password" type="password" value={passwordForm.currentPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label htmlFor="new-password">New password</label>
                  <input id="new-password" type="password" value={passwordForm.newPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })} required minLength={6} />
                </div>
                <div className="form-group">
                  <label htmlFor="confirm-password">Confirm new password</label>
                  <input id="confirm-password" type="password" value={passwordForm.confirmPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })} required />
                </div>
                {passwordError && <div className="settings-error">{passwordError}</div>}
                {passwordSuccess && <div className="settings-success">Password updated.</div>}
                <button type="submit" className="settings-submit-btn" disabled={saving}>
                  {saving ? 'Updating…' : 'Update password'}
                </button>
              </form>
            </div>
          )}

          {activeSection === 'pricing' && (
            <div className="settings-section-card">
              <h2>Pricing Grid V1</h2>
              <p className="settings-desc">Mode A (dedicated trips) and Mode B (consolidated). Changes apply to WhatsApp quotes immediately.</p>

              {pricingError && <div className="settings-error">{pricingError}</div>}
              {pricingSuccess && <div className="settings-success">Pricing saved.</div>}

              {verifyResult && (
                <div className={`settings-success ${verifyResult.allPass ? '' : 'settings-error'}`} style={{ marginBottom: '1rem' }}>
                  Verification: {verifyResult.allPass ? 'All 3 tests pass' : 'Some tests failed'}
                  <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.2rem' }}>
                    {verifyResult.tests?.map((t) => (
                      <li key={t.name}>{t.pass ? '✓' : '✗'} {t.name} — {t.actual?.toLocaleString()} F (expected {t.expected?.toLocaleString()} F)</li>
                    ))}
                  </ul>
                </div>
              )}

              {pricingConfig && (
                <>
                  <label className="pricing-toggle">
                    <input
                      type="checkbox"
                      checked={pricingConfig.rainySeasonActive}
                      onChange={(e) => setPricingConfig({ ...pricingConfig, rainySeasonActive: e.target.checked })}
                    />
                    Rainy season active (+20% on dirt roads)
                  </label>

                  <h3 className="pricing-subhead">Mode A — Vehicle rates</h3>
                  <div className="pricing-grid">
                    {VEHICLE_KEYS.map((key) => (
                      <div key={key} className="pricing-vehicle-card">
                        <strong>{pricingConfig.modeA.vehicles[key]?.label || key}</strong>
                        <label>Base (FCFA)
                          <input type="number" value={pricingConfig.modeA.vehicles[key]?.baseFare || 0}
                            onChange={(e) => updateVehicle(key, 'baseFare', e.target.value)} />
                        </label>
                        <label>Rate/km
                          <input type="number" value={pricingConfig.modeA.vehicles[key]?.ratePerKm || 0}
                            onChange={(e) => updateVehicle(key, 'ratePerKm', e.target.value)} />
                        </label>
                      </div>
                    ))}
                  </div>

                  <h3 className="pricing-subhead">Coefficients</h3>
                  <div className="pricing-row">
                    <label>Dirt road ×
                      <input type="number" step="0.1" value={pricingConfig.modeA.coefficients.ruralDirtRoad}
                        onChange={(e) => updateCoeff('ruralDirtRoad', e.target.value)} />
                    </label>
                    <label>Cold chain +%
                      <input type="number" step="0.01" value={pricingConfig.modeA.coefficients.coldChain}
                        onChange={(e) => updateCoeff('coldChain', e.target.value)} />
                    </label>
                    <label>Min fare
                      <input type="number" value={pricingConfig.minimumFare}
                        onChange={(e) => setPricingConfig({ ...pricingConfig, minimumFare: Number(e.target.value) })} />
                    </label>
                  </div>

                  <h3 className="pricing-subhead">Mode B — Consolidated (FCFA per unit)</h3>
                  {['short', 'medium', 'majorCity'].map((band) => (
                    <div key={band} className="pricing-row">
                      <span>{pricingConfig.modeB.bands[band]?.label || band}</span>
                      {['crate', 'bag', 'bunch'].map((unit) => (
                        <label key={unit}>{unit}
                          <input type="number" value={pricingConfig.modeB.bands[band]?.prices?.[unit] || 0}
                            onChange={(e) => updateModeBPrice(band, unit, e.target.value)} />
                        </label>
                      ))}
                    </div>
                  ))}

                  <button type="button" className="settings-submit-btn" onClick={savePricing} disabled={saving}>
                    {saving ? 'Saving…' : 'Save pricing grid'}
                  </button>
                </>
              )}
            </div>
          )}

          {activeSection === 'areas' && (
            <div className="settings-section-card">
              <h2>Service Areas</h2>
              <p className="settings-desc">
                Correct locality coordinates used for clustering, and deactivate places that should not take new WhatsApp orders.
              </p>
              {areasError && <div className="settings-error">{areasError}</div>}
              <input
                type="text"
                className="orders-filter"
                placeholder="Filter by name, region…"
                value={areasFilter}
                onChange={(e) => setAreasFilter(e.target.value)}
                style={{ marginBottom: '1rem', width: '100%', maxWidth: '400px' }}
              />
              <div className="areas-list">
                {filteredAreas.map((a) => (
                  <div key={a.area_id} className={`area-row area-row-coords ${a.active ? '' : 'area-inactive'}`}>
                    <div className="area-row-main">
                      <strong>{a.name}</strong>
                      <span className="area-meta">{a.region} · {a.zone} · {a.area_type}</span>
                      <div className="area-coord-fields">
                        <label>
                          Lat
                          <input
                            type="number"
                            step="0.000001"
                            value={a._editLat != null ? a._editLat : (a.lat ?? '')}
                            onChange={(e) => patchAreaLocal(a.area_id, { _editLat: e.target.value })}
                            placeholder="e.g. 5.625"
                          />
                        </label>
                        <label>
                          Lng
                          <input
                            type="number"
                            step="0.000001"
                            value={a._editLng != null ? a._editLng : (a.lng ?? '')}
                            onChange={(e) => patchAreaLocal(a.area_id, { _editLng: e.target.value })}
                            placeholder="e.g. 10.255"
                          />
                        </label>
                        <label>
                          Road access
                          <select
                            value={a._editAccess || a.road_access || 'all'}
                            onChange={(e) => patchAreaLocal(a.area_id, { _editAccess: e.target.value })}
                          >
                            <option value="all">All vehicles</option>
                            <option value="paved_only">Paved only</option>
                            <option value="4x4">4x4</option>
                            <option value="seasonal">Seasonal</option>
                          </select>
                        </label>
                      </div>
                    </div>
                    <div className="area-row-actions">
                      <button
                        type="button"
                        className="entity-btn entity-edit"
                        disabled={saving}
                        onClick={() => saveAreaCoords(a)}
                      >
                        Save GPS
                      </button>
                      <button
                        type="button"
                        className={`entity-btn ${a.active ? 'entity-delete' : 'entity-edit'}`}
                        onClick={() => toggleArea(a)}
                      >
                        {a.active ? 'Deactivate' : 'Activate'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeSection === 'notifications' && (
            <div className="settings-section-card">
              <h2>Notifications</h2>
              <div className="settings-placeholder">
                <p>Coming soon — email alerts for new orders and completed trips.</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default SettingsPage;
